import type { C2LinksEnvelope } from '@/lib/mqtt/schemas/c2links';
import type { OiLiveEnvelope } from '@/lib/mqtt/schemas/oi';
import type { ProximityEnvelope } from '@/lib/mqtt/schemas/proximity';
import type { TelemetryEnvelope } from '@/lib/mqtt/schemas/telemetry';
import { isInsideOi } from '@/lib/oi/containment';
import type { DroneId } from '@/types/ids';
/**
 * Drones store — live per-drone state aggregated from telemetry + c2links + proximity + oi.
 */
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

export type Staleness = 'live' | 'stale' | 'lost';

/** Drone provenance category (cross-tenant feature). Derived from the
 *  telemetry envelope's `origin` field: own-tenant drones are `internal`,
 *  cross-tenant partner drones `partner`, ASTM F3411 Remote-ID broadcasts
 *  `netid`. Drives the fleet-list category filter + the grey map marker. */
export type DroneOrigin = 'internal' | 'partner' | 'netid';

export interface DroneLive {
  id: DroneId;
  telemetry: TelemetryEnvelope;
  c2: C2LinksEnvelope | null;
  proximity: ProximityEnvelope | null;
  oi: OiLiveEnvelope | null;
  received_at_ms: number;
  staleness: Staleness;
  /** internal (own tenant) · partner (cross-tenant) · netid (Remote-ID). */
  origin: DroneOrigin;
  /** Source tenant for `origin: 'partner'`, else null. */
  partnerTenant: string | null;
  /** Ring buffer of recent [lng, lat] positions, oldest first. Max ~60 s × 4 Hz = 240. */
  trail: [number, number][];
}

const TRAIL_MAX = 240;

/** Field-level non-null merge: a `null` value in the incoming envelope is
 *  treated as "no information" and the existing value is kept. Used to
 *  protect backfilled state (status / ops_status / battery / flight_mode)
 *  from being clobbered when the source briefly forgets it. Position
 *  fields are always non-null in a valid TelemetryEnvelope, so they're
 *  taken from the incoming envelope as a matter of course. */
function mergeTelemetry(prev: TelemetryEnvelope, next: TelemetryEnvelope): TelemetryEnvelope {
  const out: Record<string, unknown> = { ...prev };
  for (const [k, v] of Object.entries(next)) {
    if (v !== null && v !== undefined) out[k] = v;
  }
  // The legacy and output parsers each build `_meta` and classify the source
  // differently; one saying 'custom' (it can't tell) must not erase the other's
  // answer, or the aircraft's telemetry family would flip every message.
  if (prev._meta && next._meta?.apType === 'custom' && prev._meta.apType !== 'custom') {
    out._meta = { ...next._meta, apType: prev._meta.apType };
  }
  return out as TelemetryEnvelope;
}

/** Recompute `telemetry.pred1min.inside` — whether the drone's T+60s
 *  prediction falls inside its OI polygon — for the prediction marker and the
 *  Flight Ops prediction badge. The polygon arrives on the OI topic and the
 *  position on telemetry, so neither adapter can compute it alone.
 *
 *  This no longer derives OI conformance: the OI's status is the RTM
 *  authority's `rtm_state` and nothing else (ENG-213). The prediction flag
 *  stays until its own follow-up lands. No-op when there's no polygon. */
function deriveOiContainment(
  telemetry: TelemetryEnvelope,
  oi: OiLiveEnvelope | null,
): { telemetry: TelemetryEnvelope; oi: OiLiveEnvelope | null } {
  const polygon = oi?.polygon ?? null;
  const pred = telemetry.pred1min;
  if (!polygon || !pred) return { telemetry, oi };
  const predInside = isInsideOi(pred.lng, pred.lat, polygon);
  if (pred.inside === predInside) return { telemetry, oi };
  return { telemetry: { ...telemetry, pred1min: { ...pred, inside: predInside } }, oi };
}

interface DronesStore {
  byId: Record<string, DroneLive>;
  selectedId: DroneId | null;
  /** ENG-166 — set on marker hover; the map marker raises its z-index and
   *  gets a glow, mirroring `useOiStore.hoveredId`'s pattern (though unlike
   *  OI's, nothing outside the map currently reads this — FleetList has no
   *  hover-highlight wired to it yet). */
  hoveredId: DroneId | null;
  applyTelemetry: (env: TelemetryEnvelope) => void;
  /** Blank telemetry fields. `applyTelemetry` keeps the last non-null value, so
   *  this is the only way to drop a reading once its source goes away. */
  clearTelemetryFields: (id: DroneId, fields: readonly (keyof TelemetryEnvelope)[]) => void;
  applyC2: (env: C2LinksEnvelope) => void;
  applyProximity: (env: ProximityEnvelope) => void;
  applyOi: (env: OiLiveEnvelope) => void;
  markStaleness: (id: DroneId, s: Staleness) => void;
  /** Drop drones whose last telemetry is older than `maxAgeMs`. Mirrors
   *  the aircraft eviction watchdog (config.aircraft.staleMs). */
  prune: (maxAgeMs: number) => void;
  select: (id: DroneId | null) => void;
  hover: (id: DroneId | null) => void;
}

export const useDronesStore = create<DronesStore>()(
  devtools(
    subscribeWithSelector((set) => ({
      byId: {},
      selectedId: null,
      hoveredId: null,

      clearTelemetryFields: (id, fields) =>
        set((s) => {
          const prev = s.byId[id];
          if (!prev) return s;
          const telemetry: Record<string, unknown> = { ...prev.telemetry };
          for (const f of fields) telemetry[f] = null;
          return { byId: { ...s.byId, [id]: { ...prev, telemetry: telemetry as TelemetryEnvelope } } };
        }),

      applyTelemetry: (env) =>
        set((s) => {
          const id = env.drone_id;
          const prev = s.byId[id];
          const trail = prev?.trail ? prev.trail.slice() : [];
          const last = trail[trail.length - 1];
          if (!last || last[0] !== env.lng || last[1] !== env.lat) {
            trail.push([env.lng, env.lat]);
            if (trail.length > TRAIL_MAX) trail.splice(0, trail.length - TRAIL_MAX);
          }
          // Merge non-null fields from the new envelope on top of the
          // existing telemetry. This is a belt-and-suspenders guard against
          // any upstream source that briefly loses state and re-emits an
          // envelope with null backfill fields (status / ops_status /
          // battery / etc.). Without this, the UI would flicker `Active`
          // → `—` → `Active` whenever the source's internal snapshot was
          // wiped (HMR module reload, a future StrictMode mount cycle,
          // etc.). Position / heading / received_at always come from the
          // new envelope because those are never null. The source remains
          // the primary owner of merge semantics; this is just the floor.
          const mergedTelemetry: TelemetryEnvelope = prev
            ? mergeTelemetry(prev.telemetry, env)
            : env;
          // Fold the OI polygon (if known) into the new position to keep
          // pred1min.inside live every tick.
          const derived = deriveOiContainment(mergedTelemetry, prev?.oi ?? null);
          return {
            byId: {
              ...s.byId,
              [id]: {
                id: id as DroneId,
                telemetry: derived.telemetry,
                c2: prev?.c2 ?? null,
                proximity: prev?.proximity ?? null,
                oi: derived.oi,
                received_at_ms: Date.now(),
                staleness: 'live',
                // Read from the MERGED envelope, not the incoming one. The
                // output parsers set `origin`/`partner_tenant` positively
                // only (see lib/tenancy.ts), so a partner aircraft's next
                // message may legitimately omit them — reading `env` here
                // would flip it back to 'internal' and the marker would
                // strobe between grey and green every tick.
                origin: derived.telemetry.origin ?? 'internal',
                partnerTenant: derived.telemetry.partner_tenant ?? null,
                trail,
              },
            },
          };
        }),

      applyC2: (env) =>
        set((s) => {
          const cur = s.byId[env.drone_id];
          if (!cur) return s;
          return { byId: { ...s.byId, [env.drone_id]: { ...cur, c2: env } } };
        }),

      applyProximity: (env) =>
        set((s) => {
          const cur = s.byId[env.drone_id];
          if (!cur) return s;
          return { byId: { ...s.byId, [env.drone_id]: { ...cur, proximity: env } } };
        }),

      applyOi: (env) =>
        set((s) => {
          const cur = s.byId[env.drone_id];
          if (!cur) return s;
          // A fresh OI envelope carries (a possibly new) polygon — recompute
          // the T+60s prediction's containment against it.
          const derived = deriveOiContainment(cur.telemetry, env);
          return {
            byId: {
              ...s.byId,
              [env.drone_id]: { ...cur, telemetry: derived.telemetry, oi: derived.oi },
            },
          };
        }),

      markStaleness: (id, staleness) =>
        set((s) => {
          const cur = s.byId[id];
          if (!cur) return s;
          return { byId: { ...s.byId, [id]: { ...cur, staleness } } };
        }),

      prune: (maxAgeMs) =>
        set((s) => {
          const cutoff = Date.now() - maxAgeMs;
          const next: Record<string, DroneLive> = {};
          let dropped = 0;
          for (const [id, d] of Object.entries(s.byId)) {
            if (d.received_at_ms >= cutoff) next[id] = d;
            else dropped++;
          }
          if (dropped === 0) return s;
          // If the selected/hovered drone was evicted, clear it so the detail
          // panel and marker z-index don't dangle on a drone no longer on the
          // map (ENG-166: hoveredId can otherwise leak indefinitely, since a
          // pruned marker is removed before it ever gets a real mouseleave).
          const selectedId = s.selectedId && next[s.selectedId] ? s.selectedId : null;
          const hoveredId = s.hoveredId && next[s.hoveredId] ? s.hoveredId : null;
          return { byId: next, selectedId, hoveredId };
        }),

      select: (id) => set({ selectedId: id }),
      hover: (id) => set({ hoveredId: id }),
    })),
    { name: 'drones' },
  ),
);

/* ---------------- selectors ---------------- */
export const selectFleet = (s: DronesStore) => Object.values(s.byId);
export const selectDroneById = (id: string | null) => (s: DronesStore) =>
  id ? (s.byId[id] ?? null) : null;
export const selectSelectedDrone = (s: DronesStore) => (s.selectedId ? s.byId[s.selectedId] : null);

/** Drone category filter buckets for the fleet list (mockup `drone-filter-row`:
 *  All / Internal / Partner / Network ID). `partner` + `netid` are the two
 *  external buckets. */
export type DroneCategory = 'all' | DroneOrigin;

/** Telemetry-source label for the fleet card badge. Prefers the envelope's
 *  explicit `source_label`; falls back to the autopilot type for internal
 *  drones (mockup `fi-src`: MAVLink / DJI Flight Hub / …). */
export function sourceLabel(d: DroneLive): string {
  if (d.telemetry.source_label) return d.telemetry.source_label;
  if (d.origin === 'partner') return 'Partner Stream';
  if (d.origin === 'netid') return 'Network ID';
  switch (telemetryFamily(d.telemetry)) {
    case 'liveads':
      return 'LiveADS';
    case 'dronesense':
      return 'DroneSense';
    case 'dji':
      return 'DJI Flight Hub';
    case 'mavlink':
      return 'MAVLink';
    default:
      return 'Telemetry';
  }
}

/** Which telemetry source an aircraft comes from — decides which rows the
 *  Telemetry tab shows (ENG-231's comparison matrix). `source_name` is the
 *  publisher's own `source` and is the reliable signal for the DD sources
 *  (`dji`, `fh2`, `dronesense`, `liveads`); MAVLink (ArduPilot / PX4) is told
 *  apart by the autopilot type. FH2 is the DJI OSD relayed, so one family. */
export type TelemetryFamily = 'mavlink' | 'dji' | 'dronesense' | 'liveads' | 'other';
export function telemetryFamily(
  t: Pick<TelemetryEnvelope, '_meta' | 'source_name'>,
): TelemetryFamily {
  const src = (t.source_name ?? '').toLowerCase();
  if (src.includes('liveads')) return 'liveads';
  if (src.includes('dronesense')) return 'dronesense';
  if (src.includes('dji') || src.includes('fh2')) return 'dji';
  switch (t._meta?.apType) {
    case 'ardupilot':
    case 'px4':
      return 'mavlink';
    case 'dji':
      return 'dji';
    default:
      return 'other';
  }
}

/** Header badge from real airframe state. Null when nothing is known, so the
 *  panel shows no badge rather than a made-up one. */
export function flightStateBadge(
  t: Pick<TelemetryEnvelope, 'armed' | 'landed_state' | 'adapter_connected'>,
): { label: string; variant: 'ok' | 'info' | 'muted' | 'crit' } | null {
  if (t.adapter_connected === false) return { label: 'Not connected', variant: 'crit' };
  const armed = t.armed == null ? null : t.armed ? 'Armed' : 'Disarmed';
  const label = [armed, t.landed_state].filter(Boolean).join(' · ');
  if (!label) return null;
  const airborne = t.landed_state === 'In air' || t.landed_state === 'Takeoff' || t.landed_state === 'Landing';
  return { label, variant: airborne ? 'ok' : t.armed === false ? 'muted' : 'info' };
}

/* ---------------- derived helpers (used by UI directly) ---------------- */
export type BadgeClass = 'ok' | 'warn' | 'crit' | 'info' | 'muted';

export function batteryClass(pct: number | null | undefined): BadgeClass {
  if (pct == null) return 'muted';
  if (pct <= 10) return 'crit';
  if (pct <= 25) return 'warn';
  return 'ok';
}

export function opsClass(opsStatus: TelemetryEnvelope['ops_status']): BadgeClass {
  switch (opsStatus) {
    case 'Normal':
      return 'ok';
    case 'Degraded':
      return 'warn';
    case 'Non-Normal':
      return 'crit';
    default:
      return 'muted';
  }
}

export function statusClass(status: TelemetryEnvelope['status']): BadgeClass {
  switch (status) {
    case 'Active':
    case 'DAA':
      return 'ok';
    case 'DAA-DEG':
      return 'warn';
    case 'SA':
    case 'LOST':
      return 'crit';
    default:
      return 'muted';
  }
}

export function aaClass(
  status: C2LinksEnvelope['aa_agent_status'],
  latency: number | null | undefined,
): BadgeClass {
  if (status === 'Offline' || status === 'N/A') return 'muted';
  if (status === 'Degraded') return 'warn';
  if (latency != null) {
    if (latency >= 400) return 'crit';
    if (latency >= 150) return 'warn';
  }
  return 'ok';
}

export function gcsClass(
  status: C2LinksEnvelope['gcs_agent_status'],
  latency: number | null | undefined,
): BadgeClass {
  if (status === 'Offline') return 'crit';
  if (status === 'N/A') return 'muted';
  if (status === 'Degraded') return 'warn';
  if (latency != null) {
    if (latency >= 300) return 'crit';
    if (latency >= 100) return 'warn';
  }
  return 'ok';
}

export function satsClass(
  sats: number | null | undefined,
  hdop: number | null | undefined,
): BadgeClass {
  if (sats == null) return 'muted';
  if (sats < 6) return 'crit';
  if (sats < 8 || (hdop ?? 0) > 1.1) return 'warn';
  return 'ok';
}

export function linkClass(status: 'online' | 'degraded' | 'offline'): BadgeClass {
  if (status === 'offline') return 'crit';
  if (status === 'degraded') return 'warn';
  return 'ok';
}
